// Drop-in, Excel-style drag-to-resize columns.
//
// Resize model: dragging a column's right handle resizes THAT column only —
// every other column keeps the width it was given, and the table's total width
// grows or shrinks to match. Width you reclaim from one column becomes empty
// space to the right of the table, not extra width for its neighbour.
//
// It deliberately does not work the other (zero-sum) way, where shrinking a
// column donates its width to the next one. That model holds the table width
// constant, which sounds tidy but means reclaimed space cascades rightwards and
// pools in the final column — so narrowing everything else silently inflates
// Actions, and no amount of dragging can win that space back.
//
// Every column gets a handle, the last one included. Pair this with a table
// sized to its content (`width: max-content`), not `width: 100%`: under
// `table-layout: fixed` a 100% width makes the browser redistribute leftover
// container width across the columns, re-creating the same pooling in CSS.
//
// Usage:
//   const colOrder = ['date', 'ledgerName', ..., 'actions']; // every column
//   <ResizableColumnsProvider storageKey="ledger-workspace-v2" columnOrder={colOrder}>
//     <table style={{ width: 'max-content', tableLayout: 'fixed' }}>
//       <thead><tr>
//         <ResizableTh columnId="date" defaultWidth={90} style={s.th}>Date</ResizableTh>
//         ...
//         <ResizableTh columnId="actions" defaultWidth={100} style={s.th}>Actions</ResizableTh>
//       </tr></thead>
//       ...
//     </table>
//   </ResizableColumnsProvider>

import React, { createContext, useContext, useState, useRef, useCallback, useEffect } from 'react';
import { colors } from '../styles/theme';
import { useColumnLayouts } from './ColumnLayoutsProvider';

type WidthMap = Record<string, number>;

// Narrow enough to tuck a column almost out of the way, but wide enough to
// still show a character or two of its header and leave the drag handle
// grabbable — below about this, a column becomes an unidentifiable sliver the
// user can't find again to widen. Applied when rendering as well as when
// dragging, so widths stored under an older, smaller minimum are pulled back up.
//
// Note it only bites if the cell's padding isn't already in the way: a cell
// can't render narrower than its own horizontal padding, so 1rem sides would
// impose their own ~33px floor. ResizableTh lifts padding onto an inner div to
// avoid exactly that.
const DEFAULT_MIN_WIDTH = 32;

// Floor for a header's content box, so a header holding a control (the
// ledger's subledger headers carry an 18px remove button) is no taller than
// one holding plain text. A floor rather than a fixed height: the row should
// still be sized by its own type, so changing the header font size changes the
// header's height the way you'd expect.
const HEADER_CONTENT_MIN_HEIGHT = 18;

interface ResizableColumnsContextValue {
  getWidth: (columnId: string, defaultWidth: number) => number;
  setWidth: (columnId: string, width: number) => void;
  commitWidths: (updates: Record<string, number>) => void;
  resetWidth: (columnId: string) => void;
}

// Total width for the <table>, given every column's id and default width.
//
// The table needs an explicit pixel width: `width: max-content` looks like the
// right way to size a table to its columns, but it makes the browser measure
// each column's longest unwrapped content and floor the column at that, which
// silently overrides narrow widths (a column asked to be 8px renders at ~136px).
// Summing the widths ourselves is the only way the declared widths are honoured.
export function useTableWidth(columns: { id: string; defaultWidth: number }[]): number {
  const ctx = useContext(ResizableColumnsContext);
  if (!ctx) throw new Error('useTableWidth must be inside ResizableColumnsProvider');
  // Same clamp ResizableTh applies when it renders, so the total matches what
  // the columns actually occupy rather than what's stored.
  return columns.reduce(
    (sum, c) => sum + Math.max(DEFAULT_MIN_WIDTH, ctx.getWidth(c.id, c.defaultWidth)),
    0,
  );
}

const ResizableColumnsContext = createContext<ResizableColumnsContextValue | null>(null);

function storageKeyFor(storageKey: string): string {
  return `erp:resizable-columns:${storageKey}`;
}

function loadWidths(storageKey: string): WidthMap {
  try {
    const raw = localStorage.getItem(storageKeyFor(storageKey));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveWidths(storageKey: string, widths: WidthMap) {
  try {
    localStorage.setItem(storageKeyFor(storageKey), JSON.stringify(widths));
  } catch {}
}

interface ProviderProps {
  storageKey: string;
  // Ordered list of column IDs. Nothing reads it since columns resize
  // independently — kept as an optional prop because it documents the table's
  // layout at the call site, and a future feature (column reordering, say)
  // would want it back.
  columnOrder?: string[];
  children: React.ReactNode;
}

export function ResizableColumnsProvider({ storageKey, children }: ProviderProps) {
  // Shared per-user store (backend + single localStorage cache). When absent,
  // fall back to the legacy per-table localStorage key.
  const store = useColumnLayouts();

  // Nothing stored for this key yet means a first render: every column falls
  // back to the defaultWidth its ResizableTh declares, so the table always
  // opens as a sensible layout rather than collapsing.
  const [widths, setWidths] = useState<WidthMap>(() => {
    if (store) {
      const fromStore = store.getLayout(storageKey);
      // If the store has nothing yet for this table, seed from any legacy
      // per-table key so existing users don't lose widths they already set.
      return Object.keys(fromStore).length ? fromStore : loadWidths(storageKey);
    }
    return loadWidths(storageKey);
  });

  // One-time import of legacy per-table widths into the shared store/backend.
  const seeded = useRef(false);
  useEffect(() => {
    if (!store || seeded.current) return;
    seeded.current = true;
    if (Object.keys(store.getLayout(storageKey)).length) return;
    const legacy = loadWidths(storageKey);
    if (Object.keys(legacy).length) store.setLayout(storageKey, legacy);
  }, [store, storageKey]);

  // Sync externally-driven layout changes (e.g. backend hydration after a
  // site-data clear) into local state. `storeLayout` identity changes only on
  // hydration or our own commits, so this won't loop.
  const storeLayout = store?.layouts[storageKey];
  useEffect(() => {
    if (storeLayout && Object.keys(storeLayout).length) {
      setWidths(prev => ({ ...prev, ...storeLayout }));
    }
  }, [storeLayout]);

  const persist = useCallback((next: WidthMap) => {
    if (store) store.setLayout(storageKey, next);
    else saveWidths(storageKey, next);
  }, [store, storageKey]);

  const getWidth = useCallback((columnId: string, defaultWidth: number) =>
    widths[columnId] ?? defaultWidth, [widths]);

  const setWidth = useCallback((columnId: string, width: number) => {
    setWidths(prev => ({ ...prev, [columnId]: width }));
  }, []);

  const commitWidths = useCallback((updates: Record<string, number>) => {
    setWidths(prev => {
      const next = { ...prev, ...updates };
      persist(next);
      return next;
    });
  }, [persist]);

  const resetWidth = useCallback((columnId: string) => {
    setWidths(prev => {
      const next = { ...prev };
      delete next[columnId];
      persist(next);
      return next;
    });
  }, [persist]);

  return (
    <ResizableColumnsContext.Provider value={{ getWidth, setWidth, commitWidths, resetWidth }}>
      {children}
    </ResizableColumnsContext.Provider>
  );
}

interface ResizableThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
  columnId: string;
  defaultWidth: number;
  minWidth?: number;
}

// Low enough to squeeze a column down to a sliver when you want the ones either
export function ResizableTh({ columnId, defaultWidth, minWidth = DEFAULT_MIN_WIDTH, style, children, ...rest }: ResizableThProps) {
  const ctx = useContext(ResizableColumnsContext);
  if (!ctx) throw new Error('ResizableTh must be inside ResizableColumnsProvider');

  const { getWidth, setWidth, commitWidths, resetWidth } = ctx;
  // Clamped on read, not just on drag: a stored width can predate the current
  // minimum (or an earlier version of the resize code), and a column that
  // renders as a 4px sliver is one the user can't grab to fix.
  const width = Math.max(minWidth, getWidth(columnId, defaultWidth));

  const [isDragging, setIsDragging] = useState(false);
  const [isHandleHovered, setIsHandleHovered] = useState(false);
  const dragState = useRef<{
    startX: number;
    startWidth: number;
  } | null>(null);

  // The dragged column simply follows the cursor, floored at minWidth. No
  // neighbour is touched, so the width you give back is genuinely released
  // rather than handed to the column on the right.
  const widthAt = (startW: number, delta: number) => Math.max(minWidth, startW + delta);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    dragState.current = { startX: e.clientX, startWidth: width };
    setIsDragging(true);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';

    const handleMouseMove = (ev: MouseEvent) => {
      if (!dragState.current) return;
      const { startX, startWidth } = dragState.current;
      setWidth(columnId, widthAt(startWidth, ev.clientX - startX));
    };

    const handleMouseUp = (ev: MouseEvent) => {
      if (dragState.current) {
        const { startX, startWidth } = dragState.current;
        commitWidths({ [columnId]: widthAt(startWidth, ev.clientX - startX) });
      }
      dragState.current = null;
      setIsDragging(false);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
  };

  const handleDoubleClick = () => resetWidth(columnId);

  // Padding moves from the cell onto an inner div. On the cell it acts as a
  // hard floor — a th can't render narrower than its own horizontal padding, so
  // 1rem sides stop any column going below ~33px no matter what width we set.
  // On an inner div it's just content, clipped by the cell's overflow like any
  // other, so a column can be dragged down to a sliver.
  const { padding, paddingLeft, paddingRight, paddingTop, paddingBottom, textAlign, ...cellStyle } = style ?? {};

  return (
    <th {...rest} style={{ ...cellStyle, padding: 0, width, position: 'relative' }}>
      {/* Flex-centred rather than a plain block: headers whose content is taller
          than their text (the subledger columns carry an 18px remove button)
          would otherwise give their inner div a taller box than the text-only
          headers, so the row's labels no longer sit on a common line. Centring
          every header in an equal-height box keeps them aligned whatever they
          contain. */}
      <div style={{
        padding, paddingLeft, paddingRight, paddingTop, paddingBottom,
        display: 'flex',
        alignItems: 'center',
        justifyContent: textAlign === 'left' ? 'flex-start' : textAlign === 'right' ? 'flex-end' : 'center',
        minHeight: HEADER_CONTENT_MIN_HEIGHT,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}>
        {children}
      </div>
      {/* Every column gets a handle, the last one included — it resizes itself
          rather than trading width with a neighbour, so there's nothing special
          about being last. Sitting flush inside the right edge (rather than
          straddling it) keeps the final column's handle from overflowing the
          table and becoming unhittable. It's absolutely positioned, so it never
          props the cell open; the 50% cap just stops it swallowing a column
          that's been squeezed down to a sliver, leaving room to grab the one
          to its left. */}
      <div
        onMouseDown={handleMouseDown}
        onDoubleClick={handleDoubleClick}
        onMouseEnter={() => setIsHandleHovered(true)}
        onMouseLeave={() => setIsHandleHovered(false)}
        title="Drag to resize · double-click to reset"
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 'min(14px, 50%)',
          cursor: 'col-resize',
          zIndex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <div style={{
          width: 3,
          height: '50%',
          borderRadius: 2,
          background: (isDragging || isHandleHovered) ? colors.primary : colors.border,
          transition: 'background 0.15s',
        }} />
      </div>
    </th>
  );
}
